import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileNav from "../components/MobileNav";
import Footer1 from "../components/Footer1";
import "./Cart.css";

const Cart = () => {
    const navigate = useNavigate();
    const [portfolio, setPortfolio] = useState([]);
    const [subtotal, setSubtotal] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    
    // VIP Contact Form
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        country: ""
    });

    // Load portfolio from local storage on mount
    useEffect(() => {
        window.scrollTo(0, 0);
        const savedPortfolio = JSON.parse(localStorage.getItem('userPortfolio')) || [];
        setPortfolio(savedPortfolio);
        
        const total = savedPortfolio.reduce((acc, item) => acc + (item.cartPrice || item.price || 0), 0);
        setSubtotal(total);
    }, []);

    const handleRemoveItem = (cartIdToRemove) => {
        const updatedPortfolio = portfolio.filter(item => item.cartId !== cartIdToRemove);
        setPortfolio(updatedPortfolio);
        localStorage.setItem('userPortfolio', JSON.stringify(updatedPortfolio));
        
        const total = updatedPortfolio.reduce((acc, item) => acc + (item.cartPrice || item.price || 0), 0);
        setSubtotal(total);
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // --- SUBMIT TO GOOGLE SHEETS ---
    const handleSubmitOrder = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Format items into a readable string for the spreadsheet
        const orderDetails = portfolio.map(item => 
            `${item.title} (${item.selectedSize || 'Original'} - ${item.selectedFrame || 'As is'})`
        ).join(" | ");

        const payload = {
            date: new Date().toLocaleString(),
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            country: formData.country,
            orderValue: subtotal,
            items: orderDetails
        };

        try {
            // REPLACE THIS URL with your Google Apps Script Web App URL (See instructions below)
            const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxmESPOPc_cEbhrrDeuusTxATMtbVdaPoumDTDXdO2wNwxBoTg8iKHGJnq_GFJoTeE9/exec"; 

            await fetch(GOOGLE_SCRIPT_URL, {
                method: "POST",
                mode: "no-cors", // Required for Google Scripts from frontend
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });

            // On Success: Clear cart and show luxurious success modal
            localStorage.removeItem('userPortfolio');
            setPortfolio([]);
            setSubtotal(0);
            setIsSubmitting(false);
            setShowSuccess(true);

        } catch (error) {
            console.error("Error submitting order:", error);
            alert("There was an issue securely transmitting your request. Please contact the concierge directly via WhatsApp.");
            setIsSubmitting(false);
        }
    };

    const fadeUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } }
    };

    return (
        <div className="elite-cart-page">
            <MobileNav />

            <div className="cart-container">
                <div className="cart-header">
                    <span className="curatorial-tag-gold">Your Selections</span>
                    <h1 className="cart-main-title">Private Portfolio</h1>
                </div>

                {portfolio.length === 0 && !showSuccess ? (
                    <motion.div className="empty-portfolio" initial="hidden" animate="visible" variants={fadeUp}>
                        <p>Your portfolio is currently empty.</p>
                        <button className="btn-elite-outline mt-4" onClick={() => navigate("/allPrints")}>
                            Explore The Archives
                        </button>
                    </motion.div>
                ) : !showSuccess ? (
                    <div className="cart-layout">
                        
                        {/* LEFT: Artworks List */}
                        <motion.div className="cart-items-section" initial="hidden" animate="visible" variants={fadeUp}>
                            {portfolio.map((item) => (
                                <div key={item.cartId} className="cart-item">
                                    <div className="cart-item-img">
                                        <img src={item.images?.[0]?.imageUrl || "https://via.placeholder.com/150"} alt={item.title} />
                                    </div>
                                    <div className="cart-item-details">
                                        <h3>{item.title}</h3>
                                        <span className="item-category">{item.category || "Masterwork"}</span>
                                        <div className="item-specs">
                                            {item.selectedSize && <p>Dimensions: {item.selectedSize}</p>}
                                            {item.selectedFrame && <p>Presentation: {item.selectedFrame}</p>}
                                        </div>
                                    </div>
                                    <div className="cart-item-price-action">
                                        <span className="item-price">₹ {(item.cartPrice || item.price).toLocaleString("en-IN")}</span>
                                        <button className="btn-remove" onClick={() => handleRemoveItem(item.cartId)}>
                                            <i className="fa-solid fa-times"></i> Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </motion.div>

                        {/* RIGHT: Acquisition Form */}
                        <motion.div className="cart-summary-section" initial="hidden" animate="visible" variants={fadeUp}>
                            <div className="summary-box">
                                <h2>Acquisition Summary</h2>
                                <div className="summary-row">
                                    <span>Portfolio Value</span>
                                    <span>₹ {subtotal.toLocaleString("en-IN")}</span>
                                </div>
                                <div className="summary-row highlight">
                                    <span>Total Investment</span>
                                    <span className="total-val">₹ {subtotal.toLocaleString("en-IN")}</span>
                                </div>

                                <div className="bespoke-form-divider"></div>

                                <h3>Collector Details</h3>
                                <form onSubmit={handleSubmitOrder} className="concierge-form">
                                    <input type="text" name="name" placeholder="Full Name" required onChange={handleInputChange} />
                                    <input type="email" name="email" placeholder="Email Address" required onChange={handleInputChange} />
                                    <input type="tel" name="phone" placeholder="Phone / WhatsApp Number" required onChange={handleInputChange} />
                                    <input type="text" name="country" placeholder="Country of Residence" required onChange={handleInputChange} />
                                    
                                    <button type="submit" className="btn-elite-primary submit-order-btn" disabled={isSubmitting}>
                                        {isSubmitting ? "Transmitting Request..." : "Submit Acquisition Request"}
                                    </button>
                                </form>
                                <p className="secure-notice"><i className="fa-solid fa-lock"></i> Your information is securely transmitted directly to Studio Management.</p>
                            </div>
                        </motion.div>
                    </div>
                ) : null}
            </div>

            {/* SUCCESS MODAL */}
            <AnimatePresence>
                {showSuccess && (
                    <motion.div 
                        className="success-modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div 
                            className="success-modal-content"
                            initial={{ scale: 0.9, y: 50, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            transition={{ type: "spring", damping: 25, stiffness: 120 }}
                        >
                            <i className="fa-solid fa-check-circle success-icon"></i>
                            <h2>Acquisition Requested</h2>
                            <p>Your request has been successfully transmitted to the Anjaan Vivek Studio.</p>
                            <p className="success-subtext">Our Concierge Management team will review your portfolio selection and contact you shortly via email or WhatsApp to coordinate shipping and provenance details.</p>
                            
                            <button className="btn-elite-outline" onClick={() => navigate("/")}>
                                Return to Gallery
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <Footer1 />
        </div>
    );
};

export default Cart;